// src/groups — 컨덕터 코드 DS(그룹) 원본. 데브 레포에는 **레포당 1벌**(src/ui-groups/)로 복사되고 sync 로 오간다(DECISIONS 2026-10-22·23).
//   허용 import = react · @ant-design/icons · dayjs · @musinsa/pbo-core(+/theme) · @musinsa/pbo-assets(아이콘) · 같은 폴더(./). 게이트 = check:groups-portable.
// 타이틀 행 그룹(cmp.TitleRow) — 데모 정의(skeletonBuilders buildTitleRow)와 산출물(generateAntd titleSticky 분기)이 그리던 것을 한 파일로.
//
// 구조(데모 정의 1:1): Flex(space-between·center·스티키)
//   ├ [뒤로] + 제목            ← onBack 있을 때(titleLeft) / 없으면 제목만(pageTitle)
//   ├ 제목 옆(aside)           ← 세그먼트 등. grow 로 남는 폭을 차지, 왼쪽 최종 간격 12(부모 gap 8 + margin 4)
//   └ 오른쪽: 안내 문구 + 버튼들(gap 16)
// 스티키: 래퍼에 `dev-page-header-wrap pin`(+ `sticky` 는 usePageHeaderSticky 판정) — 페인트는 pbo-core table.css 가 한다.
//   top = stickyTop − 스크롤러 패딩 변수, --pbo-page-header-bleed-top = 24 + stickyTop(페이지 패딩 위로 흰 띠를 덮는 값).
// 이동(onBack)은 화면이 한다 — 산출물에서 route 버튼은 `TODO(dev): 이동` 이었다. 그룹은 라우터를 모른다.
// 버튼 규약: 오른쪽 버튼은 size 'large'(인스턴스 팩토리 titleButtonNodes 가 넣던 값). 화면이 TITLE_BUTTON_PROPS 를 펼쳐 쓴다.
import { type ReactNode } from 'react'
import { core } from '@musinsa/pbo-core'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import { usePageHeaderSticky } from './pageHeaderSticky'

/** 타이틀 행 오른쪽 버튼 기본 — `<core.Button {...TITLE_BUTTON_PROPS} type="primary">`. */
export const TITLE_BUTTON_PROPS = { size: 'large' } as const

export type TitleRowProps = {
  title: string
  /** 뒤로가기 — 있으면 제목 왼쪽에 [←] 텍스트 버튼. 어디로 가는지는 화면(라우터)이 안다. */
  onBack?: () => void
  /** 제목 오른쪽 안내 문구(13px 보조색) — 버튼들 왼쪽에 선다. */
  notice?: string
  /** 제목 옆 자리(세그먼트·탭). */
  aside?: ReactNode
  /** 오른쪽 버튼들. 2개 이상이면 화면이 Flex gap 8 로 묶는다(팩토리 titleButtons 와 같다). */
  buttons?: ReactNode
  /** 스크롤 시 상단 고정(기본 켬). 모달·분할 패널 안에서는 끈다. */
  sticky?: boolean
  /** 고정 위치(px). 기본 12. */
  stickyTop?: number
}

export function TitleRow({ title, onBack, notice, aside, buttons, sticky = true, stickyTop = 12 }: TitleRowProps) {
  // 훅은 조건 없이 — sticky 가 꺼져도 호출 순서가 바뀌지 않게(React #310, check:hooks-order).
  const hdr = usePageHeaderSticky()
  const stickyStyle = sticky
    ? {
        flexDirection: 'row' as const,
        justifyContent: 'space-between',
        alignItems: 'center',
        height: 'auto',
        minHeight: 40,
        top: `calc(${stickyTop}px - var(--pbo-page-header-scroller-pad, 0px))`,
        ['--pbo-page-header-bleed-top' as string]: `${24 + stickyTop}px`,
      }
    : undefined
  return (
    <core.Flex gap={8} justify="space-between" align="center" ref={sticky ? hdr.ref : undefined}
      className={sticky ? `dev-page-header-wrap pin${hdr.isSticky ? ' sticky' : ''}` : undefined} style={stickyStyle}>
      {onBack ? (
        <core.Flex gap={0} align="center">
          <core.Button type="text" icon={<DsIcon name="ic-arrowLeft" size={20} color="currentColor" />} onClick={onBack} style={{ flexShrink: 0 }} />
          <core.Typography.Title level={4} style={{ margin: 0, whiteSpace: 'pre-line' }}>{title}</core.Typography.Title>
        </core.Flex>
      ) : (
        <core.Typography.Title level={4} style={{ margin: 0, whiteSpace: 'pre-line' }}>{title}</core.Typography.Title>
      )}
      {aside != null && (
        <core.Flex gap={8} align="center" style={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, marginLeft: 4 }}>
          {aside}
        </core.Flex>
      )}
      <core.Flex gap={16} align="center">
        {notice ? <core.Typography.Text style={{ fontSize: 13 }} type="secondary">{notice}</core.Typography.Text> : null}
        {buttons ?? null}
      </core.Flex>
    </core.Flex>
  )
}
